import { useEffect, useState } from 'react';
import Modal from '@v2/shared/components/Modal';
import DelayAutoRenewalForm from './DelayAutoRenewalForm';
import SuccessStep from './SuccessStep';
import { delaySuccessMessage } from './messages';
import { useAutoRenewalAction } from './useAutoRenewal';
import { AutoRenewalDetail, Props } from './types';

type ModalProps = Pick<Props, 'id' | 'loginUrl' | 'autoRenewServiceName'> & {
  isOpen: boolean;
  close: () => void;
  autoRenewal: AutoRenewalDetail;
  onUpdated: (autoRenewal: AutoRenewalDetail) => void;
};

/** The delay flow on its own — one form, then the confirmation. */
export default function DelayAutoRenewalModal({
  isOpen,
  close,
  id,
  loginUrl,
  autoRenewal,
  autoRenewServiceName,
  onUpdated,
}: ModalProps) {
  const [successMessage, setSuccessMessage] = useState<string>('');

  const delay = useAutoRenewalAction({
    id,
    loginUrl,
    action: 'delay',
    errorMessage: 'Oops, something went wrong. Try again later.',
    onSuccess: (updated) => {
      onUpdated(updated);
      setSuccessMessage(delaySuccessMessage(autoRenewServiceName, updated));
    },
  });

  // Closed and reopened is a fresh flow, not the tail of the last one.
  useEffect(() => {
    if (!isOpen) {
      setSuccessMessage('');
      delay.reset();
    }
    // `delay.reset` is stable for the life of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      title={successMessage ? 'Action Complete' : `Delay ${autoRenewServiceName}`}
    >
      {successMessage ? (
        <SuccessStep message={successMessage} onClose={close} />
      ) : (
        <DelayAutoRenewalForm
          autoRenewal={autoRenewal}
          autoRenewServiceName={autoRenewServiceName}
          onDelay={(months) => delay.run({ months })}
          loading={delay.loading}
          error={delay.error}
        />
      )}
    </Modal>
  );
}
